import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Clock, CheckCircle, RefreshCw } from 'lucide-react'
import Button from '@components/web/Button'
import { providerAuthService } from '@lib/supabase/providerAuth.service'

export default function ProviderVerificationPending() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)

  const checkStatus = async () => {
    setIsChecking(true)
    const s = await providerAuthService.getAccountStatus()
    setStatus(s)
    setIsChecking(false)
    if (s === 'approved') {
      navigate('/provider/dashboard')
    }
  }

  useEffect(() => {
    checkStatus()
    const interval = setInterval(checkStatus, 30000)
    return () => clearInterval(interval)
  }, [])

  const timeline = [
    { label: 'Account Created', done: true },
    { label: 'Email Verified', done: true },
    { label: 'Documents Submitted', done: true },
    { label: 'Under Review', done: status === 'pending_approval', active: status === 'pending_approval' },
    { label: 'Account Approved', done: status === 'approved' },
  ]

  return (
    <div className="bg-card rounded-2xl shadow-lg p-8 w-full">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Application Under Review</h2>
        <p className="text-muted-foreground text-sm mt-2">
          Our team is reviewing your credentials. This typically takes 2–3 business days.
        </p>
      </div>

      {/* Timeline */}
      <div className="space-y-3 mb-8">
        {timeline.map((step, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${step.done ? 'bg-primary text-primary-foreground' : step.active ? 'bg-primary/20 border-2 border-primary text-primary' : 'bg-muted text-muted-foreground'}`}>
              {step.done ? <CheckCircle size={14} /> : i + 1}
            </div>
            <span className={`text-sm ${step.done ? 'text-foreground font-medium' : step.active ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {step.label}
              {step.active && <span className="ml-2 text-xs text-muted-foreground">(in progress)</span>}
            </span>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        className="w-full"
        isLoading={isChecking}
        onClick={checkStatus}
        leftIcon={<RefreshCw size={16} />}
      >
        Check Status
      </Button>

      <div className="mt-6 p-4 bg-muted rounded-lg">
        <p className="text-sm font-medium text-foreground mb-1">Need help?</p>
        <p className="text-xs text-muted-foreground">
          Click the chat button to contact the provider support team.
        </p>
      </div>

      <Link to="/auth/login?role=provider" className="block text-center text-sm text-muted-foreground hover:text-foreground mt-4">
        Back to Login
      </Link>
    </div>
  )
}
